// imports
import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  Renderer2,
  input,
} from '@angular/core';

/// directive to create a masonry layout for itrating elements in a grid layout. 
// It uses the ResizeObserver and MutationObserver APIs to observe changes in the
// size of the host element and its children, and adjusts the layout accordingly.
@Directive({
  selector: '[masonry]',
  standalone: false
})
export class MasonryDirective implements AfterViewInit, OnDestroy {
  /**
   * Height of one implicit grid row, in px. The gap under a card is rounded up to a
   * multiple of this, so 1 keeps the spacing exact; raise it to trade a little
   * raggedness for fewer implicit tracks.
   */
  public masonryRowUnit = input<number>(1);

  /** Vertical space left under each card, in px. */
  public masonryGap = input<number>(16);

  private lastSpans: number[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private frame = 0;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {}

  // Set up the masonry layout and start observing the host and its children for size changes.
  ngAfterViewInit(): void {
    const host = this.el.nativeElement;

    this.renderer.addClass(host, 'masonry-js');
    this.renderer.setStyle(host, 'grid-auto-rows', `${Math.max(1, this.masonryRowUnit())}px`);
   
    this.renderer.setStyle(host, 'align-items', 'start');

    // Pure DOM bookkeeping - nothing here feeds back into change detection.
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.mutationObserver = new MutationObserver(() => this.observeChildren());
      this.mutationObserver.observe(host, { childList: true });

      this.observeChildren();
    });
  }

  //Destoring the observers when the component is destroyed to avoid memory leaks
  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  // Observe the children of the host element and schedule a layout when they change.
  private observeChildren(): void {
    if (!this.resizeObserver) {
      return;
    }

    
    this.lastSpans = [];

    this.resizeObserver.disconnect();
    this.resizeObserver.observe(this.el.nativeElement);

    for (const item of this.items()) {
      this.resizeObserver.observe(item);
    }

    this.schedule();
  }

  // Schedule a layout on the next animation frame. If a frame is already scheduled, cancel it and schedule a new one.
  private schedule(): void {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.layout());
  }
  // Layout the items in the masonry grid. This is done by calculating the number of rows each item should span based on its height
  // and the row unit, and then setting the `grid-row-end` style for each item accordingly.
  private layout(): void {
    const unit = Math.max(1, this.masonryRowUnit());
    const gap = this.masonryGap();
    const items = this.items();

    
    const spans = items.map(item =>
      Math.ceil((item.getBoundingClientRect().height + gap) / unit),
    );

    
    if (
      spans.length === this.lastSpans.length &&
      spans.every((span, index) => span === this.lastSpans[index])
    ) {
      return;
    }
    this.lastSpans = spans;

    items.forEach((item, index) => {
      this.renderer.setStyle(item, 'grid-row-end', `span ${spans[index]}`);
    });
  }

  private items(): HTMLElement[] {
    return Array.from(this.el.nativeElement.children) as HTMLElement[];
  }
}
